import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CheckCircle2, Quote, ArrowRight } from "lucide-react";
import { StarRating } from "../components/StarRating";
import { api } from "../lib/api";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; }
};

export default function ReviewsPage() {
  const [params] = useSearchParams();
  const [data, setData] = React.useState(null);
  const [step, setStep] = React.useState("verify"); // verify | write | sent
  const [form, setForm] = React.useState({
    booking_number: params.get("b") || "",
    email: params.get("e") || "",
    rating: 5,
    title: "",
    body: "",
    display_name: "",
  });
  const [busy, setBusy] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(() => {
    api.get("/reviews/public", { params: { limit: 50 } })
      .then(({ data }) => { setData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const stats = data?.stats || { count: 0, average: 0, distribution: {} };
  const items = data?.items || [];

  const verify = async (e) => {
    e?.preventDefault();
    if (!form.booking_number || !form.email) { toast.error("Udfyld begge felter"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post("/reviews/check", {
        booking_number: form.booking_number.trim().toUpperCase(),
        email: form.email.trim().toLowerCase(),
      });
      if (res.already_reviewed) {
        toast.error("Du har allerede anmeldt denne booking");
        return;
      }
      setForm((f) => ({ ...f, display_name: res.name || "" }));
      setStep("write");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke verificere");
    } finally { setBusy(false); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!form.title || form.title.length < 2) { toast.error("Tilføj en overskrift"); return; }
    if (!form.body || form.body.length < 4) { toast.error("Skriv lidt mere i din anmeldelse"); return; }
    setBusy(true);
    try {
      await api.post("/reviews", {
        booking_number: form.booking_number.trim().toUpperCase(),
        email: form.email.trim().toLowerCase(),
        rating: form.rating,
        title: form.title,
        body: form.body,
        display_name: form.display_name || undefined,
      });
      setStep("sent");
      toast.success("Tak for din anmeldelse!");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gemme anmeldelse");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="reviews-page">
      {/* Hero with score */}
      <section className="bg-brand-dark text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="kicker text-brand-red">Kundeoplevelser</div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3 leading-[1.05]">
              Verificerede anmeldelser fra rigtige kunder.
            </h1>
            <p className="text-white/70 mt-5 max-w-xl leading-relaxed">
              Vi tror på fuld åbenhed. Hver anmeldelse her er bekræftet ved en rigtig EMA Kurer-booking — ingen falske, ingen filtrerede.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white text-brand-dark p-7" data-testid="hero-score">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-bold text-6xl leading-none">{stats.average || "—"}</div>
                  <div className="text-xs uppercase tracking-widest text-brand-text2 mt-2">af 5</div>
                </div>
                <ShieldCheck className="text-[#00B67A]" size={48} />
              </div>
              <div className="mt-4"><StarRating value={Math.round(stats.average)} size={26} /></div>
              <div className="text-sm text-brand-text2 mt-3">
                {stats.count > 0
                  ? <>Baseret på <strong className="text-brand-dark">{stats.count}</strong> verificerede anmeldelser</>
                  : "Vær blandt de første til at anmelde os"}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Submit review */}
      <section className="bg-brand-bg2 border-b border-brand py-14">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12">
          {step === "verify" && (
            <form onSubmit={verify} className="bg-white border border-brand p-7 sm:p-10" data-testid="verify-form">
              <div className="kicker">Skriv en anmeldelse</div>
              <h2 className="font-display font-semibold text-2xl text-brand-dark mt-2">Verificer din booking</h2>
              <p className="text-brand-text2 mt-2 text-sm">For at sikre ægthed accepterer vi kun anmeldelser fra rigtige kunder. Indtast dit booking-nummer og email — så går vi videre.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                <div>
                  <label className="label-flat">Booking-nummer</label>
                  <input className="input-flat font-mono" value={form.booking_number} placeholder="EMA-251214-AB12CD" onChange={(e) => setForm({ ...form, booking_number: e.target.value })} required data-testid="review-booking" />
                </div>
                <div>
                  <label className="label-flat">Email</label>
                  <input type="email" className="input-flat" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="review-email" />
                </div>
              </div>
              <button type="submit" className="btn-primary mt-6" disabled={busy} data-testid="review-verify-submit">
                {busy ? <><Loader2 className="animate-spin" size={16} /> Verificerer...</> : <>Fortsæt <ArrowRight size={16} /></>}
              </button>
            </form>
          )}

          {step === "write" && (
            <form onSubmit={submitReview} className="bg-white border border-brand p-7 sm:p-10" data-testid="write-form">
              <div className="kicker">Trin 2 · Din anmeldelse</div>
              <h2 className="font-display font-semibold text-2xl text-brand-dark mt-2">Fortæl os om din oplevelse</h2>
              <div className="mt-6 space-y-5">
                <div>
                  <label className="label-flat">Din vurdering</label>
                  <div className="mt-1"><StarRating value={form.rating} size={36} interactive onChange={(r) => setForm({ ...form, rating: r })} /></div>
                </div>
                <div>
                  <label className="label-flat">Overskrift</label>
                  <input className="input-flat" maxLength={120} placeholder="Fx Hurtig og venlig service" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="review-title" />
                </div>
                <div>
                  <label className="label-flat">Din anmeldelse</label>
                  <textarea rows={5} className="input-flat" maxLength={2000} placeholder="Hvad gjorde EMA Kurer godt? Var der noget vi kan blive bedre til?" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required data-testid="review-body" />
                </div>
                <div>
                  <label className="label-flat">Vist navn (valgfri)</label>
                  <input className="input-flat" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder={form.display_name || "Anonym"} data-testid="review-name" />
                  <div className="text-xs text-brand-text2 mt-1">Vi forkorter automatisk efternavnet (fx "Anna L.") for at beskytte privatlivet.</div>
                </div>
              </div>
              <button type="submit" className="btn-primary mt-7" disabled={busy} data-testid="review-submit">
                {busy ? <><Loader2 className="animate-spin" size={16} /> Sender...</> : <>Send anmeldelse <ArrowRight size={16} /></>}
              </button>
            </form>
          )}

          {step === "sent" && (
            <div className="bg-white border border-brand p-10 text-center" data-testid="review-sent">
              <div className="w-14 h-14 bg-[#00B67A] text-white mx-auto flex items-center justify-center"><CheckCircle2 size={28} /></div>
              <h2 className="font-display font-bold text-3xl text-brand-dark mt-6">Mange tak!</h2>
              <p className="text-brand-text2 mt-3">Din anmeldelse er nu offentlig og hjælper andre med at vælge EMA Kurer. Vi sætter stor pris på din feedback.</p>
              <Link to="/anmeldelser" className="btn-secondary mt-7 inline-flex">Se alle anmeldelser <ArrowRight size={16} /></Link>
            </div>
          )}
        </div>
      </section>

      {/* All reviews */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16">
        <div className="kicker">Alle anmeldelser</div>
        <h2 className="font-display font-semibold text-3xl mt-2 text-brand-dark tracking-tight">Hvad kunderne siger</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {loading && Array.from({ length: 6 }).map((_, i) => (<div key={i} className="bg-white border border-brand h-48 animate-pulse" />))}
          {!loading && items.length === 0 && (
            <div className="md:col-span-2 lg:col-span-3 bg-white border border-brand p-12 text-center">
              <Quote className="text-brand-red mx-auto" size={28} />
              <h3 className="font-display font-semibold text-2xl text-brand-dark mt-4">Ingen anmeldelser endnu</h3>
              <p className="text-brand-text2 mt-2">Vær den første til at dele din oplevelse.</p>
            </div>
          )}
          {!loading && items.map((r) => (
            <article key={r.id} className="bg-white border border-brand p-6 flex flex-col">
              <div className="flex items-center justify-between">
                <StarRating value={r.rating} />
                <span className="text-[10px] uppercase tracking-widest text-brand-text2">{fmt(r.created_at)}</span>
              </div>
              <h4 className="font-display font-semibold text-lg text-brand-dark mt-3">{r.title}</h4>
              <p className="text-brand-text2 text-sm mt-2 leading-relaxed flex-1">{r.body}</p>
              <div className="border-t border-brand mt-5 pt-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-brand-dark text-white flex items-center justify-center text-xs font-bold">
                  {(r.display_name || "A").charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-semibold text-brand-dark">{r.display_name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#00B67A] font-semibold flex items-center gap-1"><ShieldCheck size={10} /> Verificeret kunde</div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
